import React, { useState } from 'react';
import moment from 'moment/moment';
import { SelectOption } from 'global';
import { useQuery } from '@apollo/client';
import { PaymentType, EquipmentCondition } from '@trnsact/trnsact-shared-types';
import { useForm, useWatch } from 'react-hook-form';
import { AutocompleteField, DateRangeWithPeriods } from 'components/form';
import { Box, Button, FormControl, Stack, Typography } from '@mui/material';
import LocationsTreeViewDropdown from 'components/LocationsTreeViewDropdown/LocationsTreeViewDropdown';
import { MetricsVariables } from '../dashboard/ProposalsMetricsDashboard';
import { Q_LOCATIONS } from './index';
import { buildTree, getDropdownTreeSelectDefaultOptions } from '../../../../services/locationsService';

interface Props {
  contactsOptions: SelectOption[];
  onFetchMetrics: (variables: MetricsVariables['input']) => Promise<void>;
}

const initialFilterValue = {
  locationId: null,
  userProfileId: null,
  equipmentCondition: 'all' as 'all' | EquipmentCondition.New | EquipmentCondition.Used,
  saleType: 'all' as 'all' | PaymentType.Cash | PaymentType.Financing,
  to: moment().endOf('month'),
  from: moment().startOf('month'),
};

export const DashboardFilters = ({ onFetchMetrics, contactsOptions = [] }: Props) => {
  const { control, getValues, setValue, reset } = useForm({
    defaultValues: initialFilterValue,
  });

  const locationId = useWatch({ control, name: 'locationId' });
  const userProfileId = useWatch({ control, name: 'userProfileId' });

  const [locationsTree, setLocationsTree] = useState<any[]>();

  const { data: locationsDataResponse } = useQuery(Q_LOCATIONS, {
    onCompleted(locationsData) {
      if (!Array.isArray(locationsData.locations)) return;

      const options = locationsData.locations.map((location: any) => ({
        ...location,
        checked: location.locationId === locationId,
      }));

      options.push({ locationId: null, locationName: 'Unassigned', checked: locationId == null });

      setLocationsTree(buildTree(options));
    },
  });

  const treeSelectOptions = getDropdownTreeSelectDefaultOptions({
    onChange: async (selectedNode: any) => {
      const options = (locationsDataResponse?.locations ?? []).map((location: any) => ({
        ...location,
        checked: location.locationId === selectedNode.value,
      }));

      options.push({ locationId: null, locationName: 'Unassigned', checked: selectedNode.value == null });

      setLocationsTree(buildTree(options));

      setValue('locationId', selectedNode.value);
      await onFetchMetrics({
        locationId: selectedNode.value,
        equipmentCondition: toNullableFilter(getValues('equipmentCondition'), 'all'),
        saleType: toNullableFilter(getValues('saleType'), 'all'),
        to: getValues('to').format('YYYY-MM-DD'),
        from: getValues('from').format('YYYY-MM-DD'),
      });
    },
    texts: {
      placeholder: 'Choose Location',
    },
  });

  const handleSelectRange = async (range: { endDate: moment.Moment; startDate: moment.Moment }) => {
    setValue('to', range.endDate);
    setValue('from', range.startDate);

    const values = getValues();
    console.log({
      locationId: values.locationId,
      userProfileId: values.userProfileId,
      equipmentCondition: toNullableFilter(values.equipmentCondition, 'all'),
      saleType: toNullableFilter(values.saleType, 'all'),
      to: range.endDate.format('YYYY-MM-DD'),
      from: range.startDate.format('YYYY-MM-DD'),
    });
    await onFetchMetrics({
      locationId: values.locationId,
      userProfileId: values.userProfileId,
      equipmentCondition: toNullableFilter(values.equipmentCondition, 'all'),
      saleType: toNullableFilter(values.saleType, 'all'),
      to: range.endDate.format('YYYY-MM-DD'),
      from: range.startDate.format('YYYY-MM-DD'),
    });
  };

  const handleResetFilters = async () => {
    reset(
      {
        ...initialFilterValue,
        locationId: undefined,
        userProfileId: undefined,
        equipmentCondition: undefined,
        saleType: undefined,
      },
      { keepDefaultValues: false }
    );

    const options = [
      ...(locationsDataResponse?.locations ?? []).map((location: any) => ({
        ...location,
        checked: false,
      })),
      { locationId: null, locationName: 'Unassigned', checked: true },
    ];
    setLocationsTree(buildTree(options));

    await onFetchMetrics({
      to: moment().format('YYYY-MM-DD'),
      from: moment().subtract(1, 'year').format('YYYY-MM-DD'),
    });
  };

  // Helper to convert 'all' sentinel into null (removing filter)
  const toNullableFilter = (value: string | null | undefined | any, allValue: string) => {
    let val = value;

    if (val && typeof val === 'object') {
      val = value.value;
    }
    return val === allValue ? null : val;
  };

  const equipmentConditionOptions: SelectOption[] = [
    { label: 'All', value: 'all' },
    { label: 'New', value: EquipmentCondition.New },
    { label: 'Used', value: EquipmentCondition.Used },
  ];

  const saleTypeOptions: SelectOption[] = [
    { label: 'All', value: 'all' },
    { label: 'Cash', value: PaymentType.Cash },
    { label: 'Financed', value: PaymentType.Financing },
  ];

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6" component="span">
          Filters:
        </Typography>

        <Button variant="contained" onClick={handleResetFilters}>
          Reset
        </Button>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '1rem' }}>
        <DateRangeWithPeriods control={control} nameTo="to" nameFrom="from" onRangeChange={handleSelectRange} />

        <Stack sx={{ paddingTop: '12px' }}>
          <FormControl fullWidth>
            <LocationsTreeViewDropdown tree={locationsTree ?? []} treeSelectOptions={treeSelectOptions} />
          </FormControl>
        </Stack>

        <AutocompleteField
          control={control}
          name="userProfileId"
          options={contactsOptions}
          key={userProfileId ?? 'empty'}
          textFieldProps={{ variant: 'standard', label: 'Created By' }}
          extraAction={async nextValue => {
            await onFetchMetrics({
              userProfileId: nextValue?.value ?? null,
              locationId: getValues('locationId'),
              equipmentCondition: toNullableFilter(getValues('equipmentCondition'), 'all'),
              saleType: toNullableFilter(getValues('saleType'), 'all'),
              to: getValues('to').format('YYYY-MM-DD'),
              from: getValues('from').format('YYYY-MM-DD'),
            });
          }}
        />
        <AutocompleteField
          control={control}
          name="equipmentCondition"
          options={equipmentConditionOptions}
          key={'equipmentCondition'}
          textFieldProps={{ variant: 'standard', label: 'Equipment Condition' }}
          extraAction={async nextValue => {
            await onFetchMetrics({
              locationId: getValues('locationId'),
              userProfileId: getValues('userProfileId'),
              equipmentCondition: toNullableFilter(nextValue?.value ?? 'all', 'all'),
              saleType: toNullableFilter(getValues('saleType'), 'all'),
              to: getValues('to').format('YYYY-MM-DD'),
              from: getValues('from').format('YYYY-MM-DD'),
            });
          }}
        />
        <AutocompleteField
          control={control}
          name="saleType"
          options={saleTypeOptions}
          key={'saleType'}
          textFieldProps={{ variant: 'standard', label: 'Sale Type' }}
          extraAction={async nextValue => {
            await onFetchMetrics({
              locationId: getValues('locationId'),
              userProfileId: getValues('userProfileId'),
              equipmentCondition: toNullableFilter(getValues('equipmentCondition'), 'all'),
              saleType: toNullableFilter(nextValue?.value ?? 'all', 'all'),
              to: getValues('to').format('YYYY-MM-DD'),
              from: getValues('from').format('YYYY-MM-DD'),
            });
          }}
        />
      </Box>
    </Stack>
  );
};
